import { useEffect, useRef } from 'react';

/**
 * Saves scroll position continuously and restores it when `ready` becomes true.
 * Pass ready=true once the page data has loaded so it scrolls once at the right time.
 */
export const useScrollRestore = (key: string, ready: boolean) => {
  const restored = useRef(false);

  // Restore once when ready
  useEffect(() => {
    if (!ready || restored.current) return;

    const saved = sessionStorage.getItem(`scroll-${key}`);
    if (!saved) return;

    restored.current = true;
    const scrollY = parseInt(saved);
    sessionStorage.removeItem(`scroll-${key}`);
    // Small delay to let DOM update after data renders
    const timeout = setTimeout(() => window.scrollTo(0, scrollY), 50);
    return () => clearTimeout(timeout);
  }, [key, ready]);

  // Save scroll position on every scroll (throttled).
  // This captures the position BEFORE navigation starts, since the unmount
  // cleanup may run too late (after scroll has already reset to 0).
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        sessionStorage.setItem(`scroll-${key}`, String(window.scrollY));
      }, 100);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', onScroll);
    };
  }, [key]);
};
